import React, { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Edit2, X, CheckCircle, RefreshCw, Layers, Users2, AlertTriangle } from "lucide-react";
import styles from "../hrms.module.scss";
import { APIConstant } from "services/api-constant";
import { useTheme } from "context/ThemeContext";

const ax = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` }
});

export default function LeavePolicy() {
  const { T } = useTheme();
  const TYPE_BADGE = (isPaid) => isPaid
    ? { bg:`${T.green}1A`, color:T.green, label:"Paid" }
    : { bg:`${T.red}1A`, color:T.red, label:"Unpaid / LOP" };

  const [types,     setTypes]     = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [syncing,   setSyncing]   = useState(false);
  const [applying,  setApplying]  = useState(false);
  const [toast,     setToast]     = useState("");
  const [editId,    setEditId]    = useState(null);
  const [editForm,  setEditForm]  = useState({});
  const [addOpen,   setAddOpen]   = useState(false);
  const [addForm,   setAddForm]   = useState({ name:"", default_days:0, max_days:0, is_paid:true, color:"#00C8FF", allow_half_day:true, carry_forward:false });

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3500); };

  const load = async () => {
    try {
      setLoading(true);
      const [tRes, tmRes] = await Promise.all([
        axios.get(APIConstant.leaveTypes, ax()),
        axios.get(`${APIConstant.leaveTypes}templates`, ax()),
      ]);
      setTypes(tRes.data    || []);
      setTemplates(tmRes.data || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const applyTemplates = async () => {
    setApplying(true);
    try {
      const r = await axios.post(`${APIConstant.leaveTypes}apply-templates`, {}, ax());
      flash(`✅ ${r.data.message}`);
      load();
    } catch(e) { flash("❌ " + (e.response?.data?.detail || "Error")); }
    finally { setApplying(false); }
  };

  const syncBalances = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${APIConstant.leaveTypes}sync-balances`, {}, ax());
      flash(`✅ ${r.data.message}`);
    } catch(e) { flash("❌ Error syncing"); }
    finally { setSyncing(false); }
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${APIConstant.leaveTypes}${editId}`, editForm, ax());
      flash("✅ Updated — balances synced to all employees");
      setEditId(null); load();
    } catch(e) { flash("❌ " + (e.response?.data?.detail || "Error")); }
  };

  const addType = async () => {
    if (!addForm.name) return;
    try {
      await axios.post(APIConstant.leaveTypes, addForm, ax());
      flash("✅ Leave type created and assigned to all employees");
      setAddOpen(false); load();
    } catch(e) { flash("❌ " + (e.response?.data?.detail || "Error")); }
  };

  const toggleActive = async (lt) => {
    try {
      await axios.put(`${APIConstant.leaveTypes}${lt.id}`, { is_active: !lt.is_active }, ax());
      flash(`✅ ${lt.name} ${lt.is_active ? "deactivated" : "activated"}`);
      load();
    } catch(e) { flash("❌ Error"); }
  };

  const inp = { width:"100%", background:T.inp_bg, border:`1px solid ${T.border}`, borderRadius:8, color:T.text, fontSize:13, padding:"8px 12px", outline:"none", fontFamily:"Outfit,sans-serif", boxSizing:"border-box" };
  const lbl = { fontSize:11, color:T.muted, display:"block", marginBottom:4, textTransform:"uppercase", letterSpacing:"0.07em" };

  return (
    <div className={styles.pageRoot}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.badge}>ATTENDANCE & LEAVE</div>
          <h2>Leave Policy</h2>
          <p>Configure leave types, allocations and sync to employees</p>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.btnSecondary} onClick={syncBalances} disabled={syncing}>
            <RefreshCw size={13}/> {syncing ? "Syncing…" : "Sync to All Employees"}
          </button>
          <button className={styles.btnSecondary} onClick={applyTemplates} disabled={applying}
            style={{ color:T.purple, borderColor:`${T.purple}4D` }}>
            <Layers size={13}/> {applying ? "Applying…" : "Apply Predefined Templates"}
          </button>
          <button className={styles.btnPrimary} onClick={() => setAddOpen(true)}>
            <Plus size={13}/> Add Leave Type
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className={`${styles.statsGrid} ${styles.cols4}`}>
        {[
          { label:"Total Types",  val:types.length,                              color:"cyan"   },
          { label:"Active",       val:types.filter(t=>t.is_active).length,       color:"green"  },
          { label:"Paid Leaves",  val:types.filter(t=>t.is_paid).length,         color:"purple" },
          { label:"Employees",    val:types[0]?.employee_count||0,               color:"amber"  },
        ].map(({ label, val, color }) => (
          <div key={label} className={`${styles.statCard} ${styles[color]}`}>
            <div className={styles.statVal}>{val}</div>
            <div className={styles.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      {/* Predefined templates hint */}
      {types.length === 0 && !loading && (
        <div style={{ background:`${T.purple}0F`, border:`1px solid ${T.purple}26`, borderRadius:12, padding:"20px 24px", marginBottom:20, display:"flex", gap:16, alignItems:"center" }}>
          <Layers size={28} style={{ color:T.purple, flexShrink:0 }}/>
          <div>
            <div style={{ fontSize:14, fontWeight:600, color:T.text, marginBottom:4 }}>No leave types configured yet</div>
            <div style={{ fontSize:13, color:T.muted }}>Click <strong style={{ color:T.purple }}>"Apply Predefined Templates"</strong> to instantly add 9 standard leave types (Casual, Sick, Earned, Maternity, Paternity, Marriage, Bereavement, Comp Off, LOP) with default allocations.</div>
          </div>
        </div>
      )}

      {/* Leave types table */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h3>Leave Types</h3>
          <span className={styles.cardMeta}>{types.filter(t=>t.is_active).length} active · changes auto-sync to all employees</span>
        </div>
        {loading ? <div className={styles.loader}><div className={styles.spinner}/>Loading…</div> : (
          <div className={styles.tableWrap}>
            <table className={styles.darkTable}>
              <thead>
                <tr><th>Leave Type</th><th>Days / Year</th><th>Max Days</th><th>Type</th><th>Half Day</th><th>Carry Forward</th><th>Usage</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {types.length === 0 ? (
                  <tr className={styles.emptyRow}><td colSpan={9}>No leave types — add one or apply templates</td></tr>
                ) : types.map(lt => {
                  const badge = TYPE_BADGE(lt.is_paid);
                  const isEditing = editId === lt.id;
                  return (
                    <tr key={lt.id} style={{ opacity: lt.is_active ? 1 : 0.5 }}>
                      <td>
                        <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                          <div style={{ width:10, height:10, borderRadius:"50%", background:lt.color, flexShrink:0 }}/>
                          <span style={{ fontWeight:600 }}>{lt.name}</span>
                        </div>
                      </td>
                      <td>
                        {isEditing ? (
                          <input type="number" min={0} max={365} value={editForm.default_days}
                            onChange={e => setEditForm(p=>({...p,default_days:parseInt(e.target.value)||0}))}
                            style={{ ...inp, width:70, padding:"5px 8px" }}/>
                        ) : (
                          <span style={{ fontFamily:"JetBrains Mono,monospace", fontWeight:700, color:T.cyan }}>
                            {lt.default_days === 0 ? "Unlimited" : lt.default_days}
                          </span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input type="number" min={0} value={editForm.max_days}
                            onChange={e => setEditForm(p=>({...p,max_days:parseInt(e.target.value)||0}))}
                            style={{ ...inp, width:70, padding:"5px 8px" }}/>
                        ) : (
                          <span style={{ fontFamily:"JetBrains Mono,monospace", color:T.muted }}>{lt.max_days || "—"}</span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <select value={String(editForm.is_paid)} onChange={e=>setEditForm(p=>({...p,is_paid:e.target.value==="true"}))}
                            style={{ ...inp, width:100, padding:"5px 8px" }}>
                            <option value="true">Paid</option>
                            <option value="false">Unpaid</option>
                          </select>
                        ) : (
                          <span style={{ fontSize:11.5, padding:"3px 9px", borderRadius:20, fontWeight:600, background:badge.bg, color:badge.color }}>{badge.label}</span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <select value={String(editForm.allow_half_day)} onChange={e=>setEditForm(p=>({...p,allow_half_day:e.target.value==="true"}))}
                            style={{ ...inp, width:80, padding:"5px 8px" }}>
                            <option value="true">Yes</option><option value="false">No</option>
                          </select>
                        ) : (
                          <span style={{ color:lt.allow_half_day?T.green:T.dim, fontSize:12 }}>{lt.allow_half_day?"✓ Yes":"No"}</span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <select value={String(editForm.carry_forward)} onChange={e=>setEditForm(p=>({...p,carry_forward:e.target.value==="true"}))}
                            style={{ ...inp, width:80, padding:"5px 8px" }}>
                            <option value="true">Yes</option><option value="false">No</option>
                          </select>
                        ) : (
                          <span style={{ color:lt.carry_forward?T.amber:T.dim, fontSize:12 }}>{lt.carry_forward?"✓ Yes":"No"}</span>
                        )}
                      </td>
                      <td>
                        {lt.employee_count > 0 ? (
                          <div style={{ fontSize:11.5, color:T.muted }}>
                            <span style={{ color:T.red }}>{lt.total_used}</span> / <span style={{ color:T.text }}>{lt.total_allocated}</span> days used
                          </div>
                        ) : <span style={{ color:T.dim, fontSize:12 }}>—</span>}
                      </td>
                      <td>
                        <span style={{ fontSize:11.5, padding:"3px 9px", borderRadius:20, fontWeight:600,
                          background:lt.is_active?`${T.green}1A`:T.toggle_bg,
                          color:lt.is_active?T.green:T.muted }}>
                          {lt.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <div style={{ display:"flex", gap:5 }}>
                          {isEditing ? (
                            <>
                              <button className={styles.btnSm} style={{ color:T.green, borderColor:`${T.green}40` }} onClick={saveEdit}><CheckCircle size={12}/></button>
                              <button className={styles.btnSm} onClick={() => setEditId(null)}><X size={12}/></button>
                            </>
                          ) : (
                            <>
                              <button className={styles.btnSm} onClick={() => { setEditId(lt.id); setEditForm({ default_days:lt.default_days, max_days:lt.max_days, is_paid:lt.is_paid, allow_half_day:lt.allow_half_day, carry_forward:lt.carry_forward }); }}>
                                <Edit2 size={11}/>
                              </button>
                              <button className={styles.btnSm} style={{ color:lt.is_active?T.red:T.green, borderColor:lt.is_active?`${T.red}40`:`${T.green}40` }}
                                onClick={() => toggleActive(lt)}>
                                {lt.is_active ? "Deactivate" : "Activate"}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Available templates */}
      <div className={styles.card} style={{ marginTop:16 }}>
        <div className={styles.cardHeader}><h3>Predefined Templates</h3><span className={styles.cardMeta}>Industry standard leave types</span></div>
        <div style={{ display:"flex", flexWrap:"wrap", gap:10, padding:"16px 22px" }}>
          {templates.map(t => {
            const exists = types.some(lt => lt.name === t.name);
            return (
              <div key={t.name} style={{
                padding:"10px 16px", borderRadius:10, fontSize:13,
                background: exists?`${T.green}0F`:T.toggle_bg,
                border:`1px solid ${exists?`${T.green}33`:T.toggle_bg}`,
                display:"flex", alignItems:"center", gap:9,
              }}>
                <div style={{ width:8, height:8, borderRadius:"50%", background:t.color }}/>
                <span style={{ color: exists?T.green:T.text, fontWeight:500 }}>{t.name}</span>
                <span style={{ fontSize:11, color:T.muted }}>{t.default_days ? `${t.default_days}d` : "Unlimited"}</span>
                {exists && <CheckCircle size={12} style={{ color:T.green }}/>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Add modal */}
      {addOpen && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9998, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"Outfit,sans-serif" }}
          onClick={() => setAddOpen(false)}>
          <div style={{ background:T.card, border:`1px solid ${T.cyan}33`, borderRadius:14, width:480, overflow:"hidden" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, background:`${T.cyan}0A`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <h3 style={{ margin:0, fontSize:16, fontWeight:600, color:T.text }}>Add Leave Type</h3>
              <button onClick={() => setAddOpen(false)} style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}><X size={16}/></button>
            </div>
            <div style={{ padding:24, display:"flex", flexDirection:"column", gap:14 }}>
              <div><label style={lbl}>Leave Type Name *</label>
                <input value={addForm.name} onChange={e=>setAddForm(p=>({...p,name:e.target.value}))} placeholder="e.g. Study Leave" style={inp}/>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div><label style={lbl}>Days Per Year</label>
                  <input type="number" min={0} value={addForm.default_days} onChange={e=>setAddForm(p=>({...p,default_days:parseInt(e.target.value)||0}))} style={inp}/>
                </div>
                <div><label style={lbl}>Max Days</label>
                  <input type="number" min={0} value={addForm.max_days} onChange={e=>setAddForm(p=>({...p,max_days:parseInt(e.target.value)||0}))} style={inp}/>
                </div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
                <div><label style={lbl}>Type</label>
                  <select value={String(addForm.is_paid)} onChange={e=>setAddForm(p=>({...p,is_paid:e.target.value==="true"}))} style={inp}>
                    <option value="true">Paid</option><option value="false">Unpaid</option>
                  </select>
                </div>
                <div><label style={lbl}>Half Day</label>
                  <select value={String(addForm.allow_half_day)} onChange={e=>setAddForm(p=>({...p,allow_half_day:e.target.value==="true"}))} style={inp}>
                    <option value="true">Yes</option><option value="false">No</option>
                  </select>
                </div>
                <div><label style={lbl}>Carry Forward</label>
                  <select value={String(addForm.carry_forward)} onChange={e=>setAddForm(p=>({...p,carry_forward:e.target.value==="true"}))} style={inp}>
                    <option value="false">No</option><option value="true">Yes</option>
                  </select>
                </div>
              </div>
              <div><label style={lbl}>Color</label>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {["#00C8FF","#7C5CFF","#00E5A3","#FFB800","#FF4D6D","#7A93B4"].map(c=>(
                    <div key={c} onClick={()=>setAddForm(p=>({...p,color:c}))}
                      style={{ width:24, height:24, borderRadius:"50%", background:c, cursor:"pointer", border:addForm.color===c?"2px solid #fff":"2px solid transparent" }}/>
                  ))}
                </div>
              </div>
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", paddingTop:8, borderTop:`1px solid ${T.border}` }}>
                <button onClick={()=>setAddOpen(false)} className={styles.btnSecondary}>Cancel</button>
                <button onClick={addType} className={styles.btnPrimary} disabled={!addForm.name}>Create & Assign to All Employees</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position:"fixed", bottom:28, left:"50%", transform:"translateX(-50%)", padding:"10px 22px", borderRadius:10, fontSize:13, fontWeight:600, zIndex:9999,
          background:T.card, border:`1px solid ${toast.startsWith("✅")?`${T.green}4D`:`${T.red}4D`}`,
          color:toast.startsWith("✅")?T.green:T.red, boxShadow:T.shadow, fontFamily:"Outfit,sans-serif" }}>
          {toast}
        </div>
      )}
    </div>
  );
}
